import { transaction } from '@src/slide/domain/slide-app';
import { SlideCommentConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/comment/slide-comment-converter';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ISlideModel, SlideModelSdkTypeV3 } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { CommentType } from '@tencent/slide-sdk-model';
import { getCookieUid } from '@tencent/tencent-doc-report';
import { observable } from 'mobx';
import { v4 as uuidV4 } from 'uuid';
import { SelectedModelType } from '../../../selection/selection-service.interface';
import { addComment, batchQueryComments, deleteComment, getComments, updateComment } from '../api';
import { CommentOp, ICommentGroup, ICommentWithVipInfo, INotifyCommentData, PendingCommentGroup } from '../type';
import { CommentActionType, CommentGroupInfoCache, CommentGroupInfoMap, IAddCommentGroup, IBaseCommentManager, IBaseCommentManagerServiceDelegates, ICommentActionCallbackProps, ICommentGroupIdMap } from './index.interface';

export class BaseCommentManager implements IBaseCommentManager {
    @observable public activeGroupId: string | undefined;   // 当前激活的批注组id
    @observable public hoverGroupId: string | undefined;    // 当前hover的批注组id
    @observable public isCommentAnchorShown = true;   // 是否显示批注角标，默认展示
    @observable public isCommentNotifyFlagShown = true; // 新批注红点提醒
    public commentGroupIdsMap: ICommentGroupIdMap = new Map();

    @observable protected hoverElementId: string | undefined;   // 编辑区hover的批注角标对应的元素id
    @observable protected commentGroupInfoCache: CommentGroupInfoCache = new Map(); // 缓存所有PPT的批注信息，避免重复拉取接口
    protected services: IBaseCommentManagerServiceDelegates;
    protected pendingCommentGroup?: PendingCommentGroup;
    protected uid = getCookieUid();
    protected collabAddCommentCount = 0; //  协同产生的新的批注，即其他用户所新增的批注
    protected collabUpdateCommentCount = 0; //  协同产生的新的批注，即其他用户所更新的批注
    protected collabDelCommentCount = 0; //  协同产生的新的批注，即其他用户所删除的批注

    public setServiceDelegates(services: IBaseCommentManagerServiceDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    public setHoverGroupId = (hoverGroupId: string | undefined) => {
        this.hoverGroupId = hoverGroupId;
    };

    public setActiveGroupId = (activeGroupId: string | undefined) => {
        this.activeGroupId = activeGroupId;
    };

    public setIsCommentAnchorShown(isCommentAnchorShown: boolean): void {
        this.isCommentAnchorShown = isCommentAnchorShown;
    }

    public setCommentNotifyFlagShown(isCommentNotifyFlagShown: boolean): void {
        this.isCommentNotifyFlagShown = isCommentNotifyFlagShown;
    }

    public setHoverElementId(hoverElementId: string | undefined): void {
        this.hoverElementId = hoverElementId;
    }

    /**
     * 激活元素对应的批注组的第n个批注组
     */
    public setActiveGroupIdForElement(elementId: string, activeGroupIndex = 0) {
        const ids = this.commentGroupIdsMap.get(elementId);
        if (ids && ids.length > 0) {
            const activeGroupId = ids[activeGroupIndex];
            activeGroupId && this.setActiveGroupId(activeGroupId);
        }
    }

    /**
     * 获取批注后台缓存的批注内容
     */
    public getComments = async (anchorId: string): Promise<ICommentWithVipInfo[] | undefined> => {
        const slideId = this.getSlideId();
        if (!slideId) return;
        return await getComments(anchorId, slideId);
    };

    /**
     * 添加新的批注组
     */
    public addCommentGroup = (): IAddCommentGroup | undefined => {
        const pendingCommentGroup = this.addPendingCommentGroup();
        if (!pendingCommentGroup) return;
        const newCommentGroupInfoMap = new Map([[pendingCommentGroup.groupId, []]]);
        const commentGroupInfoMap = this.getCurrentSlide()?.commentGroupInfoMap as CommentGroupInfoMap;
        return {
            commentGroupInfoMap: commentGroupInfoMap
                ? new Map([...commentGroupInfoMap, ...newCommentGroupInfoMap])
                : newCommentGroupInfoMap,
            pendingCommentGroup,
        };
    };

    /**
     * 添加批注：先请求批注后台接口，再调用sdk接口。避免批注后台接口调用失败，导致批注组没有对应的批注内容
     */
    public addComment = async (anchorId: string, content: string): Promise<undefined> => {
        const slideId = this.getSlideId();
        if (!slideId) return;

        try {
            const commentInfo = await addComment(anchorId, slideId, content);
            this.updateCacheByAdd(anchorId, commentInfo);
            let isCommentGroup = false;
            // 表明是新的批注组，需要调用sdk接口
            if (!this.getCurrentSlide()?.commentGroupMap?.get(anchorId)) {
                if (this.pendingCommentGroup?.groupId !== anchorId) return;
                const commentGroup = SlideCommentConverter
                    .convertCommentGroupUiToSdk(this.pendingCommentGroup.commentGroup);
                this.getCommentSdkApi()?.addComments([{ groupId: anchorId, commentGroup }]);
                this.setPendingCommentGroup(undefined);
                isCommentGroup = true;
            }
            this.afterCommentActionCallback({ commentActionType: CommentActionType.Add, isCommentGroup });
        } catch (error) {
            console.error('addComment error: ', error);
        }
    };

    /**
     * 更新批注：更新批注后台内容，无需调用sdk接口
     */
    public updateComment = async (commentId: string, anchorId: string, content: string): Promise<undefined> => {
        const slideId = this.getSlideId();
        if (!slideId) return;
        const commentInfo = await updateComment(commentId, anchorId, slideId, content);
        this.updateCacheByUpdate(anchorId, commentInfo);

        // 判断是否为首条批注
        const firstCommentId = this.commentGroupInfoCache.get(slideId)?.get(anchorId)?.[0].id;
        this.afterCommentActionCallback({ commentActionType: CommentActionType.Update,
            isFirstComment: firstCommentId === commentId });
    };

    /**
     * 删除单条批注：更新批注后台，无需调用sdk接口
     */
    public deleteComment = async (anchorId: string, commentId: string): Promise<undefined> => {
        const slideId = this.getSlideId();
        if (!slideId) return;
        await deleteComment(anchorId, commentId, slideId);
        this.updateCacheByDelete(anchorId, commentId);
        this.afterCommentActionCallback({ commentActionType: CommentActionType.Delete });
    };

    /**
     * 删除批注组：仅调用sdk接口，批注后台不做删除，考虑修订记录的情况。
     */
    public deleteCommentGroup = (anchorId: string): void => {
        const slideId = this.getSlideId();
        if (!slideId) return;
        const commentGroup = this.getCurrentSlide()?.commentGroupMap?.get(anchorId);
        if (!commentGroup) return;
        this.getCommentSdkApi()?.removeComments([
            {
                groupId: anchorId,
                commentGroup: { commentType: commentGroup.commentType, shapeId: commentGroup.shapeId },
            },
        ]);
        this.updateCacheByDelete(anchorId);
    };

    /**
     * 删除当前页所有批注
     */
    public deletePageComment = (): void => {
        const slideId = this.getSlideId();
        if (!slideId) return;
        this.getCommentSdkApi()?.removeAllComments();
        this.updateCacheByDeletePageComment();
    };

    public deleteAllComment = (): void => {
        transaction(() => {
            this.getPresentationSdk()?.slideList.forEach((slideSdk) => {
                slideSdk.api.removeAllComments();
            });
        });
        this.commentGroupInfoCache = new Map();
    };

    public jumpToPrevComment = () => {
        eventBus.emit(EventKeys.ACTIVE_COMMENT_CHANGE, {
            isJumpToPrevComment: true,
        });
    };

    public jumpToNextComment = () => {
        eventBus.emit(EventKeys.ACTIVE_COMMENT_CHANGE);
    };

    /**
     * 获取文档批注总数
     */
    public getCommentCount = (): number => this.getCurrentSlide()?.parent?.children
        .reduce((acc, current: ISlideModel) => acc + (current.commentGroupInfoMap?.size ?? 0), 0) ?? 0;

    /**
     * 是否是当前批注组的owner。默认创建第一条批注的用户为owner，同时文档owner是所有批注组的owner，即拥有操作该批注组的权限
     */
    public isCommentGroupOwner = (groupId: string): boolean => {
        // 文档作者拥有批注组的权限
        const { isOwner } = window.clientVars;
        if (isOwner) return true;

        const slideId = this.getSlideId();
        if (!slideId) return false;
        const uin = this.commentGroupInfoCache.get(slideId)?.get(groupId)?.[0].uin;
        if (!uin) return false;
        // 检查当前用户是否为创建第一条批注的用户
        return (window.clientVars.userInfo.uid ?? getCookieUid()) === uin;
    };

    public getCommentGroupInfoMap = (slideId: string | null): CommentGroupInfoMap | undefined => {
        if (slideId) {
            return this.commentGroupInfoCache.get(slideId);
        }
    };

    /**
     * 调用批注接口之后的回调
     * @param commentActionType 接口来源
     * @param isCommentGroup 是否是批注组还是单条批注
     * @param needRefreshDrawer 是否需要刷新侧边栏
     */
    protected afterCommentActionCallback = (props: ICommentActionCallbackProps) => {
    };

    /**
     * 更新元素id和批注组id的对应关系
     */
    protected updateCommentGroupIdMap = () => {
        const slideId = this.getSlideId();
        if (!slideId) return;
        this.commentGroupIdsMap = new Map();

        const commentGroupMap = this.getCurrentSlide()?.commentGroupMap;
        commentGroupMap?.forEach((commentGroup, groupId) => {
            const id = commentGroup.shapeId ? commentGroup.shapeId : slideId;
            if (!id) return;
            if (!this.commentGroupIdsMap.get(id)) {
                this.commentGroupIdsMap.set(id, []);
            }
            this.commentGroupIdsMap.get(id)!.push(groupId);
        });
    };

    /**
     * 接收批注后台的推送，更新缓存
     * @param payload 批注后台推送的内容
     */
    protected updateCommentCacheByNotice = async (payload: { op: CommentOp, comment: INotifyCommentData[] }) => {
        const { comment, op } = payload;
        this.collabAddCommentCount = 0;
        this.collabUpdateCommentCount = 0;
        this.collabDelCommentCount = 0;
        switch (op) {
            case CommentOp.add:
                await this.batchUpdateCacheByAdd(comment);
                break;
            case CommentOp.del:
                this.batchUpdateCacheByDelete(comment);
                break;
            case CommentOp.mod:
                this.batchUpdateCacheByUpdate(comment);
                break;
            case CommentOp.clear:
                // 删除当前批注组会产生mutation，会走sdk-ui的逻辑，此处无需处理
                break;
            default:
                break;
        }
    };

    /**
     * 批量拉取批注后台，更新批注内容缓存
     */
    protected updateCommentGroupInfoMap = async (currentSlide = this.getCurrentSlide()) => {
        const currentSlideId = currentSlide?.id;
        if (!currentSlideId) return;
        const commentGroupMap = currentSlide?.commentGroupMap;
        if (!commentGroupMap || commentGroupMap.size === 0) {
            this.commentGroupInfoCache.delete(currentSlideId);
            this.updateCurrentCommentGroupInfoMap(currentSlide);
            return;
        }

        // 获取需要拉取后台接口的groupId，同时如果有缓存则从缓存中读取数据
        const commentGroupInfoMap = this.commentGroupInfoCache.get(currentSlideId);
        const updatedCommentGroupInfoMap = new Map();
        const toUpdateGroupIds: string[] = [];
        commentGroupMap.forEach((commentGroup, groupId) => {
            if (!commentGroup) return;
            const commentGroupInfoList = commentGroupInfoMap?.get(groupId);
            if (commentGroupInfoList) {
                updatedCommentGroupInfoMap.set(groupId, commentGroupInfoList);
            } else {
                toUpdateGroupIds.push(groupId);
                updatedCommentGroupInfoMap.set(groupId, []);
            }
        });

        // 请求后台接口
        if (toUpdateGroupIds.length > 0) {
            const result = await batchQueryComments(toUpdateGroupIds);
            result.forEach((value, groupId) => {
                updatedCommentGroupInfoMap.set(groupId, value);
            });
        }
        this.commentGroupInfoCache.set(currentSlideId, updatedCommentGroupInfoMap);
        this.updateCurrentCommentGroupInfoMap(currentSlide);
    };

    // 选中元素时，侧边栏高亮对应的批注。 拿到一个modelId，高亮所有group里对应的shapeId
    // 选中group，拿出group的shapeId，高亮shape
    // 元素还要显示当前批注的数量
    /**
     * 获取当前选中元素的批注组的id
     */
    protected getSelectedCommentGroupIdList = (): string[] => {
        const elementId = this.getSelectedModelId();
        if (!elementId) return [];
        const commentGroupMap = this.getCurrentSlide()?.commentGroupMap;
        const groupIdList: string[] = [];
        commentGroupMap?.forEach((commentGroup, groupId) => {
            if (elementId === commentGroup.shapeId) {
                groupIdList.push(groupId);
                return;
            }
        });
        return groupIdList;
    };

    protected getCurrentSlide = () => this.services.selectionService.currentSlide;

    protected addPendingCommentGroup = (): { groupId: string, commentGroup: ICommentGroup } | undefined => {
        this.setPendingCommentGroup(undefined);
        const selectedModel = this.getSelectedModel();
        if (!selectedModel) return;

        const commentGroupMap = this.getCurrentSlide()?.commentGroupMap;
        let groupId = '';
        do {
            groupId = `{${uuidV4().toUpperCase()}}`;
        } while (commentGroupMap?.get(groupId));
        // 当前页批注
        if (selectedModel.type === UIModelType.SLIDE) {
            this.pendingCommentGroup = { groupId, commentGroup: { commentType: CommentType.slide } };
        } else {
            // 元素批注
            const shapeId = selectedModel.id ?? selectedModel.sdkData?.id;
            if (shapeId) {
                this.pendingCommentGroup = { groupId, commentGroup: { commentType: CommentType.element, shapeId } };
            }
        }
        return this.pendingCommentGroup;
    };

    /**
     * 是支持添加批注
     */
    protected isSupportAddComment = (selectedModel = this.getSelectedModel()): boolean => {
        const type = selectedModel?.type;
        return !!(type && [
            UIModelType.SHAPE, UIModelType.CONNECTOR, UIModelType.IMAGE,
            UIModelType.GROUP, UIModelType.CHART, UIModelType.TABLE, UIModelType.SLIDE,
        ].includes(type));
    };

    protected getSelectedModel = (): SelectedModelType | ISlideModel => {
        const selectedModel = this.services.selectionService.getSelectedModel();
        return selectedModel?.type !== UIModelType.EMPTY ? selectedModel : this.services.selectionService.currentSlide;
    };

    protected getSelectedModelId = (): string | undefined => {
        const selectedModel = this.getSelectedModel();
        return selectedModel?.id ?? selectedModel?.sdkData?.id;
    };

    protected setPendingCommentGroup = (pendingCommentGroup: PendingCommentGroup) => {
        this.pendingCommentGroup = pendingCommentGroup;
    };

    /**
     * 获取当前页的所有批注组
     */
    private updateCurrentCommentGroupInfoMap = (currentSlide = this.getCurrentSlide()) => {
        const currentSlideId = currentSlide?.id;
        if (!currentSlideId) return;
        const validCache = new Map();
        const currentSlideInfoCache = this.commentGroupInfoCache.get(currentSlideId);
        currentSlideInfoCache?.forEach((infoList, groupId) => {
            // 避免批注后台批注信息不存在
            if (infoList.length > 0) {
                validCache.set(groupId, infoList);
            }
        });
        currentSlide.commentGroupInfoMap = validCache;
        eventBus.emit(EventKeys.COMMENT_GROUP_INFO_CHANGED);
    };

    /**
     * 添加批注时，更新缓存
     */
    private updateCacheByAdd = (anchorId: string, commentInfo: ICommentWithVipInfo, toUpdateSlideId?: string) => {
        const slideId = toUpdateSlideId ?? this.getSlideId();
        if (!slideId) return;

        if (!this.commentGroupInfoCache.get(slideId)) {
            this.commentGroupInfoCache.set(slideId, new Map());
        }
        const oldCommentInfoList = this.commentGroupInfoCache.get(slideId)!.get(anchorId) ?? [];
        this.commentGroupInfoCache.get(slideId)!.set(anchorId, [...oldCommentInfoList, commentInfo]);
        this.getCurrentSlide()!.commentGroupInfoMap = this.commentGroupInfoCache.get(slideId);
        const currentSlide = this.getCurrentSlide()?.parent?.children
            .find(slide => slide.id === slideId) as ISlideModel;
        currentSlide && this.updateCurrentCommentGroupInfoMap(currentSlide);
    };

    private batchUpdateCacheByAdd = async (commentList: INotifyCommentData[]) => {
        const anchorList: string[] = [];
        commentList.forEach((commentData: INotifyCommentData) => {
            const { anchor, author } = commentData;
            // 如果是当前用户触发的更新，不做处理
            if (author === this.uid) return;
            this.collabAddCommentCount += 1;
            if (!anchorList.includes(anchor)) {
                anchorList.push(anchor);
            }
        });
        if (anchorList.length === 0) return;
        const result = await batchQueryComments(anchorList);
        commentList.forEach((commentData) => {
            const { anchor, commentid } = commentData;
            const commentInfo = result.get(anchor)?.find(info => info.id === commentid);
            if (commentInfo) {
                const slideId = commentData.sheet_id;
                this.updateCacheByAdd(anchor, commentInfo, slideId);
            }
        });
    };

    /**
     * 更新批注时，更新缓存
     */
    private updateCacheByUpdate = (anchorId: string, commentInfo: ICommentWithVipInfo) => {
        const slideId = this.getSlideId();
        if (!slideId || !this.commentGroupInfoCache.get(slideId)) return;
        const oldCommentInfoList = this.commentGroupInfoCache.get(slideId)!.get(anchorId);
        let commentInfoList = [commentInfo];
        if (oldCommentInfoList && oldCommentInfoList.length > 0) {
            commentInfoList = oldCommentInfoList.map(info => (info.id === commentInfo.id ? commentInfo : info));
        }
        this.commentGroupInfoCache.get(slideId)!.set(anchorId, commentInfoList);
        this.getCurrentSlide()!.commentGroupInfoMap = this.commentGroupInfoCache.get(slideId);
    };

    /**
     * 删除单条或者整组批注时，更新缓存
     */
    private updateCacheByDelete = (anchorId: string, commentId?: string, toUpdateSlideId?: string) => {
        const slideId = toUpdateSlideId ?? this.getSlideId();
        if (!slideId) return;
        const oldCommentInfoList = this.commentGroupInfoCache.get(slideId)?.get(anchorId);
        if (!oldCommentInfoList || oldCommentInfoList.length === 0) return;
        if (!commentId) {
            // 删除整个批注组
            this.commentGroupInfoCache.get(slideId)?.delete(anchorId);
        } else {
            // 删除单条批注组
            const index = oldCommentInfoList.findIndex(info => info.id === commentId);
            if (index !== -1) {
                oldCommentInfoList.splice(index, 1);
            }
            this.commentGroupInfoCache.get(slideId)?.set(anchorId, oldCommentInfoList);
        }
        this.getCurrentSlide()!.commentGroupInfoMap = this.commentGroupInfoCache.get(slideId);
        const currentSlide = this.getCurrentSlide()?.parent?.children
            .find(slide => slide.id === slideId) as ISlideModel;
        currentSlide && this.updateCurrentCommentGroupInfoMap(currentSlide);
    };

    /**
     * 删除当前页批注，更新缓存
     */
    private updateCacheByDeletePageComment = () => {
        const slideId = this.getSlideId();
        if (!slideId) return;
        this.commentGroupInfoCache.delete(slideId);
        this.updateCurrentCommentGroupInfoMap();
    };

    private batchUpdateCacheByDelete = (commentList: INotifyCommentData[]) => {
        commentList.forEach((commentData) => {
            const { anchor, commentid, author, sheet_id: slideId } = commentData;
            // 如果是当前用户触发的更新，不做处理
            if (author === this.uid) return;
            this.collabDelCommentCount += 1;
            this.updateCacheByDelete(anchor, commentid, slideId);
        });
    };

    private batchUpdateCacheByUpdate = (commentList: INotifyCommentData[]) => {
        commentList.forEach((commentData: INotifyCommentData) => {
            const { anchor, commentid, author, sheet_id: slideId } = commentData;
            // 如果是当前用户触发的更新，不做处理
            if (author === this.uid) return;
            this.collabUpdateCommentCount += 1;
            const slideCommentInfo = this.commentGroupInfoCache.get(slideId);
            const commentInfo = slideCommentInfo?.get(anchor)?.find(value => value.id === commentid);
            if (commentInfo) {
                this.updateCacheByUpdate(anchor, commentInfo);
            }
        });
    };

    private getPresentationSdk = () => (this.getCurrentSlide()?.sdkData as SlideModelSdkTypeV3)?.parent;

    private getCommentSdkApi = () => (this.getCurrentSlide()?.sdkData as SlideModelSdkTypeV3)?.api;

    private getSlideId = () => this.getCurrentSlide()?.id;
}
